import BracketsListingApp from "./BracketsListingApp.vue";
import brackets, { type BracketsSharingState } from "../../Api/brackets";
import disciplinePackagesApi from "../../Api/disciplinePackages";
import { vueAppWithPinia } from "../../Common/vueApp";
import { ComplexBracket } from "../../Common/Brackets/types";
import objectMatcher from "../../Common/objectMatcher";
import { defineStore } from "pinia";

export const useBracketsListingStore = defineStore("brackets-listing", {
    state: () => {
        return {
            eventId: 0,
            complexBrackets: [] as ComplexBracket[],
            disciplinesNotConfigured: false,
            noDisciplinesDetected: false,
            uncoveredCategories: 0,
            bulkSelectionEnabled: false,
            selectedParameterIds: {} as Record<number, boolean>,
            sharing: null as BracketsSharingState | null,
            searchQuery: "",
            showOutdatedBrackets: false,
            showDisqualifiedBrackets: false,
        };
    },
    getters: {
        filteredComplexBrackets: (state): ComplexBracket[] =>
            state.complexBrackets
                .filter(cb => cb.isPersisted)
                .filter(cb => objectMatcher.matchString(state.searchQuery, cb.name))
                .filter(cb => !state.showOutdatedBrackets || cb.isOutdated)
                .filter(cb => !state.showDisqualifiedBrackets || cb.disqualifiedCompetitors.length > 0),
        totalOutdatedBrackets(): number {
            return this.filteredComplexBrackets.filter(cb => cb.isOutdated).length;
        },
        bracketsWithDisqualifications(): number {
            return this.filteredComplexBrackets.filter(cb => cb.disqualifiedCompetitors.length > 0).length;
        },
        allVisibleSelected(): boolean {
            return (
                this.filteredComplexBrackets.length > 0 &&
                this.filteredComplexBrackets.every(cb => this.selectedParameterIds[cb.parameter_id])
            );
        },
        totalSelectedCount: state => Object.values(state.selectedParameterIds).filter(Boolean).length,
        selectedParameterIdsList: state =>
            Object.entries(state.selectedParameterIds)
                .filter(([, selected]) => selected)
                .map(([parameterId]) => Number(parameterId)),
    },
    actions: {
        refreshBracketsListing() {
            return brackets.getComplexBrackets(this.eventId, true).then(complexBrackets => {
                this.complexBrackets = complexBrackets;
                // Drop selections of brackets that no longer exist (deleted one by one or in bulk). A deleted bracket's
                // category stays in the list as a non-persisted entry, so only persisted ones count.
                Object.keys(this.selectedParameterIds).forEach(parameterId => {
                    if (!complexBrackets.some(cb => cb.isPersisted && cb.parameter_id === Number(parameterId))) {
                        delete this.selectedParameterIds[Number(parameterId)];
                    }
                });
            });
        },
        fetchDisciplinePackagesStatus() {
            return disciplinePackagesApi.getDisciplinePackages(this.eventId).then(pkgs => {
                const nonOrphaned = pkgs.disciplines.filter(p => !p.isOrphaned);
                this.noDisciplinesDetected = nonOrphaned.length === 0;
                this.disciplinesNotConfigured = nonOrphaned.some(p => !p.isConfigured);
                this.uncoveredCategories = pkgs.uncoveredCategories;
            });
        },
        fetchBracketsSharing() {
            return brackets.getBracketsSharing(this.eventId).then(sharing => {
                this.sharing = sharing;
            });
        },
        updateBracketsSharing(enabled: boolean) {
            return brackets.updateBracketsSharing(this.eventId, { enabled }).then(sharing => {
                this.sharing = sharing;
            });
        },
        enableBulkSelection() {
            this.bulkSelectionEnabled = true;
        },
        toggleVisibleSelection(selected: boolean) {
            this.filteredComplexBrackets.forEach(cb => {
                this.selectedParameterIds[cb.parameter_id] = selected;
            });
        },
        clearSelection() {
            this.bulkSelectionEnabled = false;
            this.selectedParameterIds = {};
        },
    },
});

export default vueAppWithPinia(BracketsListingApp);
